import type { CanvasVariant } from "../canvas/variant-resolver";
import { cheapestDisplayPrice, type DisplayPrice } from "../canvas/variant-price";
import { ASPECT_TOLERANCE, offeredSizes } from "./artwork-sizing";
import type { Artwork } from "./artwork-types";
import { matches } from "./artwork-search";

/**
 * The shop-window logic of the catalogue: what a work costs "from", which
 * shape it is, and how a list is filtered and ordered.
 *
 * Kept free of React so the rules can be tested on their own, and so the
 * gallery, the collection pages and the artwork page's "similar" rows all
 * answer these questions the same way.
 */

export type Orientation = "portrait" | "landscape" | "square";
export const ORIENTATIONS: Orientation[] = ["portrait", "landscape", "square"];

export type StoreSort = "popular" | "new" | "price-asc" | "price-desc";
export const STORE_SORTS: StoreSort[] = ["popular", "new", "price-asc", "price-desc"];
export const DEFAULT_SORT: StoreSort = "popular";

/**
 * The tags offered as colour and mood filters, in the order they are listed.
 *
 * Tags are free text on the artwork and also carry subject ("krajina") and
 * technique ("lesorez"), which the collections already cover, and sometimes
 * orientation, which is read off the pixels instead. Only this vocabulary is
 * offered as a filter, and only the ones some work in view actually has.
 * A new mood needs a line here and a label in each locale.
 */
export const MOOD_TAGS = ["mirni", "topli", "modri", "crno-belo"] as const;

/**
 * Shape from the master's pixels, not from tags.
 *
 * Within the same tolerance the size gates use counts as square, so a
 * painting the shop would print on a square format is filed as one.
 */
export function orientationOf(
  artwork: Pick<Artwork, "widthPx" | "heightPx">,
): Orientation {
  const { widthPx: w, heightPx: h } = artwork;
  if (!w || !h) return "square";
  const ratio = Math.max(w, h) / Math.min(w, h);
  if (ratio - 1 <= ASPECT_TOLERANCE) return "square";
  return w > h ? "landscape" : "portrait";
}

/**
 * The "from" price of one work: its cheapest printable size, in the market's
 * currency.
 *
 * Only sizes this master can honestly be printed at count (offeredSizes), so
 * the card never advertises a 20 × 20 a landscape painting cannot be sold as.
 * The amount and currency come as a pair from cheapestDisplayPrice, which is
 * what keeps a euro number from appearing under a złoty sign.
 */
export function priceFrom(
  artwork: Pick<Artwork, "widthPx" | "heightPx">,
  variants: CanvasVariant[] | null | undefined,
  currency: string,
): DisplayPrice | null {
  if (!variants?.length) return null;
  const offered = offeredSizes(
    { widthPx: artwork.widthPx, heightPx: artwork.heightPx },
    variants,
  ).map((o) => o.variant);
  return cheapestDisplayPrice(offered, currency);
}

export interface StoreFilters {
  orientation: Orientation | null;
  /** Colour/mood tags; a work must carry every one chosen. */
  tags: string[];
  /** Collection slug, or null for every collection. */
  collection: string | null;
  query: string;
  sort: StoreSort;
  /** Only the works hearted in this browser (see wishlist.ts). */
  saved: boolean;
}

export const EMPTY_FILTERS: StoreFilters = {
  orientation: null,
  tags: [],
  collection: null,
  query: "",
  sort: DEFAULT_SORT,
  saved: false,
};

/** Whether anything narrows the list (sorting does not). */
export function isFiltered(filters: StoreFilters): boolean {
  return (
    !!filters.orientation ||
    filters.tags.length > 0 ||
    !!filters.collection ||
    !!filters.query.trim() ||
    !!filters.saved
  );
}

const time = (value: string | undefined | null) => {
  const t = value ? Date.parse(value) : NaN;
  return Number.isNaN(t) ? 0 : t;
};

/**
 * The shop's own order: featured works first, then the admin's sortOrder.
 *
 * Array.prototype.sort is stable, so works with equal keys keep the order the
 * server returned them in.
 */
export function byPopularity(a: Artwork, b: Artwork): number {
  return (
    Number(!!b.featured) - Number(!!a.featured) ||
    (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );
}

/**
 * Filter and order a list.
 *
 * `priceOf` is passed in rather than computed here, because it depends on the
 * variants and the currency and the caller already has both. A work with no
 * known price sorts last in either price direction — it is the least useful
 * thing to put at the top of a "cheapest first" list.
 */
export function filterAndSort<T extends Artwork>(
  artworks: T[],
  filters: StoreFilters,
  options: {
    indexOf?: (artwork: T) => string;
    priceOf?: (artwork: T) => number | null;
    /** The hearted ids; `filters.saved` keeps only these. */
    savedIds?: ReadonlySet<string>;
  } = {},
): T[] {
  const { indexOf, priceOf, savedIds } = options;
  const query = filters.query.trim();

  const shown = artworks.filter(
    (a) =>
      (!filters.orientation || orientationOf(a) === filters.orientation) &&
      (!filters.collection ||
        (a.collections ?? []).some((c) => c.slug === filters.collection)) &&
      filters.tags.every((tag) => (a.tags ?? []).includes(tag)) &&
      (!filters.saved || !!savedIds?.has(a.id)) &&
      (!query || !indexOf || matches(indexOf(a), query)),
  );

  const sorted = shown.slice();
  switch (filters.sort) {
    case "new":
      sorted.sort(
        (a, b) =>
          time(b.createdAt) - time(a.createdAt) ||
          byPopularity(a, b),
      );
      break;
    case "price-asc":
    case "price-desc": {
      const dir = filters.sort === "price-asc" ? 1 : -1;
      const price = (a: T) => priceOf?.(a) ?? null;
      sorted.sort((a, b) => {
        const pa = price(a);
        const pb = price(b);
        if (pa == null && pb == null) return byPopularity(a, b);
        if (pa == null) return 1;
        if (pb == null) return -1;
        return (pa - pb) * dir || byPopularity(a, b);
      });
      break;
    }
    default:
      sorted.sort(byPopularity);
  }
  return sorted;
}

/** The mood tags at least one of these works carries, in MOOD_TAGS order. */
export function availableMoodTags(artworks: Pick<Artwork, "tags">[]): string[] {
  const present = new Set(artworks.flatMap((a) => a.tags ?? []));
  return MOOD_TAGS.filter((tag) => present.has(tag));
}

/** The orientations at least one of these works has. */
export function availableOrientations(
  artworks: Pick<Artwork, "widthPx" | "heightPx">[],
): Orientation[] {
  const present = new Set(artworks.map(orientationOf));
  return ORIENTATIONS.filter((o) => present.has(o));
}

/**
 * Works like this one: sharing a collection or a tag, most in common first.
 *
 * A shared collection weighs more than a shared tag — a collection is a
 * curator's grouping, a tag is a single attribute. Ties keep the shop's order.
 */
export function similarWorks<T extends Artwork>(
  artwork: Artwork,
  artworks: T[],
  limit = 8,
): T[] {
  const collections = new Set((artwork.collections ?? []).map((c) => c.slug));
  const tags = new Set(artwork.tags ?? []);

  return artworks
    .filter((a) => a.id !== artwork.id)
    .map((a) => ({
      a,
      score:
        2 * (a.collections ?? []).filter((c) => collections.has(c.slug)).length +
        (a.tags ?? []).filter((t) => tags.has(t)).length,
    }))
    .filter((x) => x.score > 0)
    .sort((x, y) => y.score - x.score || byPopularity(x.a, y.a))
    .slice(0, limit)
    .map((x) => x.a);
}

/** Other works by the same artist, in the shop's order. */
export function moreByArtist<T extends Artwork>(
  artwork: Artwork,
  artworks: T[],
  limit = 8,
): T[] {
  const artist = (artwork.artist ?? "").trim().toLowerCase();
  if (!artist) return [];
  return artworks
    .filter(
      (a) => a.id !== artwork.id && (a.artist ?? "").trim().toLowerCase() === artist,
    )
    .sort(byPopularity)
    .slice(0, limit);
}

/**
 * Filters to and from the URL, so a filtered view can be shared or bookmarked.
 *
 * English keys and values on every locale: the URL is the same shape whatever
 * language the page is in. Unknown values are dropped rather than trusted, so
 * a hand-edited link degrades to the unfiltered gallery instead of an empty
 * one. The collection is not here — collections have pages of their own.
 */
export function filtersFromQuery(
  params: { get: (key: string) => string | null },
): Omit<StoreFilters, "collection"> {
  const orientation = params.get("orientation");
  const sort = params.get("sort");
  const tags = (params.get("tags") ?? "")
    .split(",")
    .map((t) => t.trim())
    .filter((t) => (MOOD_TAGS as readonly string[]).includes(t));
  return {
    orientation: ORIENTATIONS.includes(orientation as Orientation)
      ? (orientation as Orientation)
      : null,
    tags: [...new Set(tags)],
    query: params.get("q") ?? "",
    sort: STORE_SORTS.includes(sort as StoreSort) ? (sort as StoreSort) : DEFAULT_SORT,
    saved: params.get("saved") === "1",
  };
}

/** The query string for a set of filters; defaults are left out. */
export function filtersToQuery(filters: Omit<StoreFilters, "collection">): string {
  const params = new URLSearchParams();
  if (filters.orientation) params.set("orientation", filters.orientation);
  if (filters.tags.length) params.set("tags", filters.tags.join(","));
  if (filters.query.trim()) params.set("q", filters.query.trim());
  if (filters.sort !== DEFAULT_SORT) params.set("sort", filters.sort);
  if (filters.saved) params.set("saved", "1");
  return params.toString();
}

/** How long a work counts as "Novo" after it was added. */
export const NEW_FOR_DAYS = 30;

export type CardBadge = "new" | "popular";

/**
 * The small labels on a card's frame: "Novo" for a work added in the last
 * NEW_FOR_DAYS days, "Priljubljeno" for one the shop features.
 *
 * Both come from facts the catalogue already holds (createdAt and the admin's
 * featured switch). Nothing here counts sales, so nothing here can claim a
 * bestseller the shop has not got. A date in the future (a clock off by a
 * day) still counts as new; an unreadable one does not.
 */
export function cardBadges(
  artwork: Pick<Artwork, "createdAt" | "featured">,
  now: number = Date.now(),
): CardBadge[] {
  const badges: CardBadge[] = [];
  const created = time(artwork.createdAt);
  if (created && now - created <= NEW_FOR_DAYS * 24 * 60 * 60 * 1000) badges.push("new");
  if (artwork.featured) badges.push("popular");
  return badges;
}
